import { RefObject, useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { Table } from '@tanstack/react-table'
import clsx from 'clsx'
import { format } from 'date-fns'
import styles from '../../Disruptions.module.scss'
import { Input } from '@/ui/Input'
import { DisruptionFormValues } from '@/pages/Disruptions/types'
import { Disruption, DisruptionStatus } from '@/types/disruptions'
import { Status } from '../Status'
import { useGetDisruptionReference } from '@/pages/Disruptions/api/useGetDisruptionReference'
import { useGetAreasRoutes } from '@/modules/Areas/hooks/useGetAreasRoutes'
import { TableFormDelayInput } from '@/modules/CombinedLogs'
import { DisruptionTypeInput } from './DisruptionTypeInput/DisruptionTypeInput'
import { DiversionRouteContainer } from './DiversionRouteSelector/DiversionRouteContainer'
import { NmccInput } from './NmccInput/NmccInput'
import { RouteSelectorContainer } from './RouteSelector/RouteSelectorContainer'
import { DateSelectContainer } from './DateSelect/DateSelectContainer'
import { Table as TableComponent, TableColGroup } from '@/ui/Table'
import { useCheckPermissions } from '@/permissions/useCheckPermissions'
import { useLocation } from 'react-router-dom'
import { ROUTES } from '@/router'
import PlusIcon from '../../../assets/plus.svg'
import WarningIcon from '../../../assets/warning.svg'
import { useDisruptionsTableFormStateStore } from '@/pages/Disruptions/store'

const DRAFT_BANNER_DURATION_MS = 10_000

export const CreateDisruptionForm = ({
    form,
    isCreateFormActive,
    setIsCreateFormActive,
    isSubmitting,
    table,
    wrapperRef,
}: {
    form: UseFormReturn<DisruptionFormValues>
    isCreateFormActive: boolean
    setIsCreateFormActive: (value: boolean) => void
    isSubmitting: boolean
    table: Table<Disruption>
    wrapperRef: RefObject<HTMLDivElement>
}) => {
    const { data: reference } = useGetDisruptionReference()
    const { areaRoutes } = useGetAreasRoutes()
    const hasPermission = useCheckPermissions()
    const { pathname } = useLocation()
    const {
        draftCreatedReference,
        clearDraftCreatedReference,
        publishedDisruptionBanner,
        clearPublishedDisruptionBanner,
    } = useDisruptionsTableFormStateStore()

    useEffect(() => {
        form.setFocus('routes')
    }, [isCreateFormActive, form])

    useEffect(() => {
        if (!draftCreatedReference) return
        const timer = setTimeout(clearDraftCreatedReference, DRAFT_BANNER_DURATION_MS)
        return () => clearTimeout(timer)
    }, [draftCreatedReference, clearDraftCreatedReference])

    useEffect(() => {
        if (!publishedDisruptionBanner) return
        const timer = setTimeout(clearPublishedDisruptionBanner, DRAFT_BANNER_DURATION_MS)
        return () => clearTimeout(timer)
    }, [publishedDisruptionBanner, clearPublishedDisruptionBanner])

    if (!hasPermission('disruption', 'create') || !pathname.includes(ROUTES.ALL_DISRUPTIONS)) {
        return null
    }

    const isDraftBanner = !!draftCreatedReference && !isCreateFormActive
    const isSuccessBanner = !!publishedDisruptionBanner && !isCreateFormActive

    return (
        <div ref={wrapperRef} className={styles.formTableWrapper}>
            <TableComponent>
                <TableColGroup table={table} />
                <tbody>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr
                            className={clsx(styles.formRow, styles.active, {
                                [styles.hidden]: !isCreateFormActive,
                            })}
                            key={headerGroup.id}
                        >
                            <td className={clsx(styles.formCell, styles.alignCenterFormCell)} key={'status'}>
                                <div className={styles.statusContainer}>
                                    <Status isHeader status={form.getValues('status') as DisruptionStatus} />
                                </div>
                            </td>
                            <td className={clsx(styles.formCell, styles.alignCenterFormCell)} key={'reference'}>
                                <div className={styles.reference}>{reference}</div>
                            </td>
                            <td className={styles.formCell} key={'routes'}>
                                <RouteSelectorContainer
                                    form={form}
                                    areaRoutes={areaRoutes.map((route) => route.route)}
                                />
                            </td>
                            <td className={styles.formCell} key={'location'}>
                                <Input
                                    {...form.register('location')}
                                    variant="table"
                                    type="text"
                                    transparent
                                    fullWidth
                                    placeholder={'Location'}
                                />
                            </td>
                            <td className={clsx(styles.formCell, styles.alignCenterFormCell)} key={'details'}>
                                <div>
                                    <Input
                                        {...form.register('details')}
                                        variant="table"
                                        type="text"
                                        transparent
                                        fullWidth
                                        placeholder={'Details'}
                                    />

                                    <DiversionRouteContainer form={form} />
                                </div>
                            </td>
                            <td className={styles.formCell} key={'disruptionType'}>
                                <DisruptionTypeInput form={form} />
                            </td>
                            <td className={styles.formCell} key={'nmccInformed'}>
                                <NmccInput form={form} />
                            </td>
                            <td className={styles.formCell} key={'delayInMinutes'}>
                                <TableFormDelayInput name="delayInMinutes" control={form.control} />
                            </td>

                            <DateSelectContainer form={form} />

                            <td className={styles.formCell} key={'menu'}></td>
                        </tr>
                    ))}

                    <tr
                        className={clsx({
                            [styles.addDisruptionPlaceholderRow]: !isDraftBanner && !isSuccessBanner && !isSubmitting,
                            [styles.addDisruptionLoadingRow]: isSubmitting,
                            [styles.addDisruptionDraftRow]: isDraftBanner,
                            [styles.addDisruptionSuccessRow]: isSuccessBanner,
                            [styles.hidden]: isCreateFormActive,
                        })}
                        onClick={
                            !isDraftBanner && !isSuccessBanner && !isSubmitting
                                ? () => setIsCreateFormActive(true)
                                : undefined
                        }
                    >
                        {isSuccessBanner ? (
                            <td className={styles.disruptionStatusCell} colSpan={table.getAllLeafColumns().length}>
                                <div className={clsx(styles.disruptionStatusContent, styles.successStatusContent)}>
                                    <WarningIcon />
                                    <p className={clsx(styles.disruptionStatusText)}>
                                        <span className={styles.disruptionStatusBold}>
                                            Disruption {publishedDisruptionBanner.reference} added -{' '}
                                        </span>
                                        Disruption {publishedDisruptionBanner.reference} starting at{' '}
                                        {format(publishedDisruptionBanner.start, 'HH:mm')} on{' '}
                                        {format(publishedDisruptionBanner.start, 'dd/MM/yy')} published to controllers.
                                    </p>
                                    <div className={styles.disruptionStatusActions}>
                                        <button
                                            className={clsx(styles.disruptionStatusButton, styles.successStatusButton)}
                                            onClick={() => {
                                                clearPublishedDisruptionBanner()
                                                setIsCreateFormActive(true)
                                            }}
                                        >
                                            <PlusIcon />
                                            Add another
                                        </button>
                                    </div>
                                </div>
                            </td>
                        ) : isDraftBanner ? (
                            <td className={styles.disruptionStatusCell} colSpan={table.getAllLeafColumns().length}>
                                <div className={clsx(styles.disruptionStatusContent, styles.draftStatusContent)}>
                                    <WarningIcon />
                                    <p className={clsx(styles.disruptionStatusText)}>
                                        <span className={styles.disruptionStatusBold}>
                                            Disruption {draftCreatedReference} created as draft -{' '}
                                        </span>
                                        Disruptions must have a start time, location and description to be visible to
                                        controllers.
                                    </p>
                                    <div className={styles.disruptionStatusActions}>
                                        <button
                                            className={clsx(styles.disruptionStatusButton, styles.draftStatusButton)}
                                            onClick={() => {
                                                clearDraftCreatedReference()
                                                setIsCreateFormActive(true)
                                            }}
                                        >
                                            <PlusIcon />
                                            Add another
                                        </button>
                                    </div>
                                </div>
                            </td>
                        ) : (
                            <td
                                className={styles.addDisruptionPlaceholderCell}
                                colSpan={table.getAllLeafColumns().length}
                            >
                                <span>
                                    <PlusIcon />
                                    Add a Disruption
                                </span>
                            </td>
                        )}
                    </tr>
                </tbody>
            </TableComponent>
        </div>
    )
}
